import { ShieldAlert, ShieldCheck, ShieldOff } from "lucide-react";

export interface ResumeIntakeFields {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | null;
  firm_name?: string | null;
  jd_year?: number | null;
  law_school?: string | null;
  practice_area?: string | null;
  location?: string | null;
  linkedin_url?: string | null;
  gpa?: string | null;
}

// task 2dc337d2 Slice 2 — intake now runs as a background AiJobStatus job;
// the initial POST response only carries the job id, not the parsed fields.
export interface ResumeIntakeResponse {
  ai_job_status_id?: number;
  resume_blob_id?: string;
}

// Shape of AiJobStatus#result once the intake job reaches "ready" — the
// finding #38 INV-1 boundary: this payload is the only place parsed fields
// live until the user reviews and saves the form themselves.
export interface ResumeIntakeResult {
  fields: ResumeIntakeFields;
  warnings: string[];
}

export interface DncLog {
  layer: string;
  result: string;
  checked_at: string | null;
  notes: string | null;
}

export interface Submission {
  id: number;
  firm_name: string;
  firm_contact_name: string;
  submission_method: string;
  status: string;
  submitted_at: string | null;
  created_at: string;
  notes: string;
  job_id: number | null;
  job_title: string | null;
  cover_letter_generated: boolean;
  candidate_approved: boolean;
}

export type SkmBlastContactStatus = "queued" | "sent" | "responded" | "interested" | "not_interested" | "bounced";

export interface SkmBlastContact {
  id: number;
  contact_name: string;
  firm_name: string;
  status: SkmBlastContactStatus;
  sent_at: string | null;
  responded_at: string | null;
  response_notes: string;
}

export interface SkmBlast {
  id: number;
  status: string;
  sent_at: string | null;
  created_at: string;
  contact_count: number;
  profile_generated: boolean;
  contacts: SkmBlastContact[];
}

export interface Candidate {
  id: number;
  first_name: string;
  last_name: string;
  preferred_first_name: string;
  full_name: string;
  email: string;
  phone: string;
  firm_name: string;
  candidate_type: string;
  jd_year: number | null;
  law_school: string;
  undergrad: string;
  gpa: string;
  practice_area: string;
  specialties: string;
  location: string;
  admitted: string;
  all_admissions: string;
  languages: string;
  degrees: string;
  honors: string;
  industries: string;
  origin_firm: string;
  profile_url: string;
  linkedin_url: string;
  pipeline_stage: string;
  priority: string;
  source: string;
  dnc_status: string;
  dnc_overridden: boolean;
  dnc_override_reason: string | null;
  dnc_override_at: string | null;
  dnc_sheet_checked_at: string | null;
  dnc_exchange_checked_at: string | null;
  resume_state: string;
  submission_status: string;
  notes: string;
  intake_date: string | null;
  days_in_stage: number;
  excluded_firms: string[];
  lifecycle_state: "active" | "inactive" | "archived";
  archived_reason: string | null;
  lifecycle_changed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PackageSummary {
  id: number;
  status: string;
  submissions_count: number;
  skm_contacts_count: number;
  all_sections_complete: boolean;
}

// tier/rationale are null for the legacy umbrella-practice-area fallback
// (candidate has no persisted JobMatch rows yet) — new tiers (strong/
// possible) and legacy pre-rematch values (high/medium) both appear here,
// same folding as JobAlerts/Show.
export interface MatchingJob {
  id: number;
  title: string;
  firm_name: string;
  practice_area: string;
  location: string;
  tier: "strong" | "possible" | "high" | "medium" | null;
  rationale: string | null;
}

export interface Props {
  candidate: Candidate;
  dnc_logs: DncLog[];
  submissions: Submission[];
  skm_blasts: SkmBlast[];
  resume_url: string | null;
  package_summary: PackageSummary | null;
  matching_jobs: MatchingJob[];
  practice_area_options: string[];
  ai_assist: boolean;
}

export type Tab = "overview" | "resume" | "submissions" | "skm" | "activity" | "email" | "cover_letters" | "exclusions";

export const STAGE_LABELS: Record<string, string> = {
  new_lead: "New Lead", intake_scheduled: "Intake Scheduled",
  resume_editing: "Resume Editing", package_pending: "Package Pending",
  submitted: "Submitted", screener: "Screener",
  full_round: "Full Round", offer_placed: "Offer / Placed",
};

export const SUBMISSION_STATUS_BADGE: Record<string, string> = {
  drafted:      "bg-gray-100 text-gray-600",
  submitted:    "bg-blue-100 text-blue-700",
  screening:    "bg-yellow-100 text-yellow-700",
  full_round:   "bg-purple-100 text-purple-700",
  offer_placed: "bg-green-100 text-green-700",
  rejected:     "bg-red-100 text-red-600",
  withdrawn:    "bg-gray-100 text-gray-500",
};

export const SKM_CONTACT_STATUS_BADGE: Record<SkmBlastContactStatus, string> = {
  queued:         "bg-gray-100 text-gray-500",
  sent:           "bg-blue-100 text-blue-700",
  responded:      "bg-yellow-100 text-yellow-700",
  interested:     "bg-green-100 text-green-700",
  not_interested: "bg-red-50 text-red-500",
  bounced:        "bg-red-100 text-red-700 border border-red-300",
};

export const DNC_CONFIG: Record<string, { label: string; icon: typeof ShieldCheck; className: string }> = {
  clear:            { label: "Outreach Eligible",  icon: ShieldCheck, className: "text-green-600" },
  blocked_sheet:    { label: "Blocked (Sheet)",    icon: ShieldAlert, className: "text-red-500" },
  blocked_exchange: { label: "Blocked (Exchange)", icon: ShieldAlert, className: "text-orange-500" },
  blocked_both:     { label: "Blocked (Both)",     icon: ShieldOff,   className: "text-red-700" },
};

export const SOURCE_LABELS: Record<string, string> = {
  leopard: "Leopard", linkedin: "LinkedIn", inbound: "Inbound",
  referral: "Referral", cold_email_response: "Cold Email Response",
};

export function DncBadge({ status, overridden }: { status: string; overridden: boolean }) {
  const config = DNC_CONFIG[status] ?? DNC_CONFIG.clear;
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-sm font-medium ${config.className}`}>
      <Icon className="w-4 h-4" />
      {config.label}
      {overridden && <span className="text-xs text-gray-400 ml-1">(overridden)</span>}
    </span>
  );
}

export function Field({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (!value && value !== 0) return null;
  return (
    <div>
      <dt className="text-xs text-gray-500 uppercase tracking-wide">{label}</dt>
      <dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
    </div>
  );
}

export function fmt(iso: string | null) {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}
